import { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Typography, Tooltip, Skeleton, Theme, useTheme } from '@mui/material';
import Humanize from 'humanize-plus';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';
import { ReactComponent as ArrowRight } from '../../../assets/arrow-right.svg';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon.svg';
import { Flex, FlexCenter, FlexColumn } from '../../../components/Flex';
import useHover from '../../../hooks/useHover';
import { formatShortDate } from '../../../utils/datetime';
import { ChartData } from '../types';
import { themeTokens } from '../../../style/tokens';
import { fontSize, fontWeight, radius, space } from '../../../style/utils';

const Wrapper = styled(FlexColumn)<{ width: number; height: number }>`
  border: 1px solid ${({ theme }) => theme.palette.border.controlStrong};
  border-radius: ${radius('md')};
  width: ${({ width }) => width}px;
  max-width: 100%;
  height: ${({ height }) => height}px;
`;

const TitleWrapper = styled(Flex)`
  padding: ${space('lg')};
  justify-content: space-between;
  align-items: center;
`;

const Title = styled(Typography)`
  font-weight: ${fontWeight('medium')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const SeeResults = styled(Typography)`
  font-size: ${fontSize('md')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.palette.surface.emphasis};
`;

const ArrowRightStyled = styled(ArrowRight)`
  path {
    stroke: ${({ theme }) => theme.palette.icon.secondary};
  }
`;

const AxisTickText = styled.text`
  fill: ${({ theme }) => theme.palette.icon.secondary};
`;

const CustomXAxisTick = ({ x, y, payload }: any) => {
  const formattedDate = formatShortDate(payload.value);
  return (
    <AxisTickText
      x={x}
      y={y}
      dy={10}
      fontSize={11}
      textAnchor="middle"
      aria-label={`x-axis-tick-${formattedDate}`}
    >
      {formattedDate}
    </AxisTickText>
  );
};

const CustomYAxisTick = ({ x, y, payload }: any) => {
  const formattedValue =
    payload.value < 1000 && payload.value % 1
      ? payload.value.toFixed(2)
      : Humanize.compactInteger(payload.value, 1).replace('.0', '');
  return (
    <AxisTickText
      x={x}
      y={y}
      dx={-12}
      dy={4}
      fontSize={11}
      textAnchor="middle"
      aria-label={`y-axis-tick-${formattedValue}`}
    >
      {formattedValue}
    </AxisTickText>
  );
};

const getStatusColors = ({ palette }: Theme) => ({
  success: {
    stroke: palette.chart.status.success,
    fill: palette.status.success.bgSoft,
  },
  fail: {
    stroke: palette.chart.status.danger,
    fill: palette.status.error.bgSoft,
  },
  warning: {
    stroke: palette.chart.status.warning,
    fill: palette.status.warning.bgStrong,
  },
});

type Props = {
  data: ChartData;
  xAxisDataKey: string;
  areaDataKey: string;
  width: number;
  height?: number;
  title: string;
  color: 'success' | 'fail' | 'warning';
  unit: string;
  domainY?: AxisDomain;
  tooltip: string;
  tooltipFormatter?: (val: any) => string | number;
  yAxisDataMaxOverride?: number;
  onSeeResultsClick?: () => void;
  loading?: boolean;
};

const DashboardChart = ({
  data,
  xAxisDataKey,
  areaDataKey,
  width,
  height = 260,
  title,
  color,
  unit,
  domainY,
  tooltip,
  tooltipFormatter,
  yAxisDataMaxOverride,
  onSeeResultsClick,
  loading,
}: Props) => {
  const theme = useTheme();
  const titleRef = useRef();
  const isTitleHover = useHover(titleRef);
  const statusColors = getStatusColors(theme)[color];

  const domainYProp: AxisDomain = useMemo(() => {
    if (domainY) {
      return domainY;
    }

    if (yAxisDataMaxOverride) {
      return [
        0,
        Math.ceil(yAxisDataMaxOverride / 0.7) < 1
          ? 1
          : Math.ceil(yAxisDataMaxOverride / 0.7),
      ];
    }

    return ([, dataMax]: [number, number]) => [
      0,
      Math.ceil(dataMax / 0.7) < 1 ? 1 : Math.ceil(dataMax / 0.7),
    ];
  }, [domainY, yAxisDataMaxOverride]);

  return (
    <Wrapper
      width={width}
      height={height}
      divider={<Divider />}
      ref={titleRef as any}
    >
      <TitleWrapper>
        <Flex sx={{ gap: 1, alignItems: 'center' }}>
          <Title>{title}</Title>
          <Tooltip
            title={tooltip}
            sx={{ visibility: isTitleHover ? 'visible' : 'hidden' }}
          >
            <Flex>
              <InfoIcon />
            </Flex>
          </Tooltip>
        </Flex>
        {isTitleHover && onSeeResultsClick && !loading && (
          <Flex
            sx={{ gap: 1, alignItems: 'center', cursor: 'pointer' }}
            onClick={onSeeResultsClick}
          >
            <SeeResults>See results</SeeResults>
            <ArrowRightStyled />
          </Flex>
        )}
      </TitleWrapper>

      <FlexCenter sx={{ height: '100%' }}>
        {loading ? (
          <Skeleton
            variant="rectangular"
            width="calc(100% - 40px)"
            height="calc(100% - 40px)"
            sx={{ borderRadius: radius('md') }}
          />
        ) : (
          <ResponsiveContainer width="100%" height={height - 70}>
            <AreaChart
              data={data}
              margin={{ top: 32, right: 24, left: 20, bottom: 0 }}
              aria-label={`${title} chart`}
            >
              <XAxis
                dataKey={xAxisDataKey}
                tick={CustomXAxisTick}
                tickLine={false}
                axisLine={false}
                aria-label={`${title} x-axis`}
              />
              <YAxis
                width={30}
                tick={CustomYAxisTick}
                tickLine={false}
                axisLine={false}
                domain={domainYProp}
                label={{
                  value: unit,
                  fontSize: themeTokens.fontSize.xs,
                  position: 'top',
                  offset: 18,
                  marginLeft: 100,
                  fill: theme.palette.icon.tertiary,
                }}
              />
              <CartesianGrid strokeOpacity={0.5} />
              <ChartTooltip
                formatter={(val) => tooltipFormatter?.(val) || val}
              />
              <Area
                type="linear"
                dataKey={areaDataKey}
                stroke={statusColors.stroke}
                fill={statusColors.fill}
                fillOpacity={1}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </FlexCenter>
    </Wrapper>
  );
};

export default DashboardChart;
